import { useState } from 'react'
import { useTheme } from '../../contexts/ThemeContext'
import { fonts } from '../../lib/themes'

const TIPOS = [
  {
    tipo: 'meta',
    emoji: '🎯',
    nombre: 'Meta',
    desc: 'ahorra para algo específico y luego gástalo cuando llegues al objetivo.',
    caracteristicas: ['un monto claro y una fecha', 'fase de ahorro y después de gasto', 'historial de aportes'],
  },
  {
    tipo: 'viaje',
    emoji: '✈️',
    nombre: 'Viaje',
    desc: 'planea tu viaje con presupuesto distribuido por categorías.',
    caracteristicas: ['vuelos, hotel, comida, actividades...', 'control de gasto por área', 'sigue cada peso del viaje'],
  },
  {
    tipo: 'proyecto',
    emoji: '🔨',
    nombre: 'Proyecto',
    desc: 'financia un proyecto personal o profesional con presupuesto interno.',
    caracteristicas: ['divide por equipo, marketing, legal...', 'fase de ahorro y ejecución', 'seguimiento detallado'],
  },
  {
    tipo: 'compromiso',
    emoji: '⚖️',
    nombre: 'Compromiso',
    desc: 'registra una deuda y sigue tu avance hasta saldarla por completo.',
    caracteristicas: ['empieza en negativo', 'trabaja hacia cero', 'ajustable por intereses o cargos'],
  },
  {
    tipo: 'guardado',
    emoji: '🔄',
    nombre: 'Guardado',
    desc: 'ahorra cíclicamente para gastos que se repiten — navidad, seguros, vacaciones anuales.',
    caracteristicas: ['define frecuencia: mensual, anual...', 'se recarga con cada ciclo', 'anticipa gastos predecibles'],
  },
  {
    tipo: 'fondo',
    emoji: '🛡️',
    nombre: 'Fondo',
    desc: 'construye y protege una reserva de largo plazo — emergencias, retiro, inversiones.',
    caracteristicas: ['objetivo opcional o aspiracional', 'sin fase de gasto obligatoria', 'crecimiento a largo plazo'],
  },
]

export default function MetaTipoPicker({ onSelect, onClose }) {
  const { theme } = useTheme()
  const s = makeStyles(theme)
  const [selected, setSelected] = useState(null)
  const info = TIPOS.find(t => t.tipo === selected) || null

  return (
    <div style={s.screen}>
      {/* Top bar */}
      <div style={s.topBar}>
        <button style={s.closeBtn} onClick={onClose}>←</button>
        <div>
          <h1 style={s.heading}>elige tu meta</h1>
        </div>
      </div>

      {/* Pill row */}
      <div style={s.pillRow}>
        {TIPOS.map(({ tipo, emoji, nombre }) => (
          <button
            key={tipo}
            style={{ ...s.pill, ...(selected === tipo ? s.pillActive : {}) }}
            onClick={() => setSelected(tipo)}
          >
            <span style={s.pillEmoji}>{emoji}</span>
            <span style={s.pillLabel}>{nombre}</span>
          </button>
        ))}
      </div>

      {/* Detail card */}
      <div style={s.detailWrap}>
        {info ? (
          <div style={s.detailCard}>
            <div style={s.detailTop}>
              <span style={s.detailEmoji}>{info.emoji}</span>
              <div>
                <h2 style={s.detailNombre}>{info.nombre}</h2>
                <p style={s.detailDesc}>{info.desc}</p>
              </div>
            </div>
            <div style={s.detailDivider} />
            <ul style={s.detailList}>
              {info.caracteristicas.map((c, i) => (
                <li key={i} style={s.detailItem}>
                  <span style={s.detailDot}>·</span> {c}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div style={s.emptyCard}>
            <p style={s.emptyText}>selecciona un tipo para ver sus características</p>
          </div>
        )}
      </div>

      {/* Continue button */}
      <div style={s.footer}>
        <button
          style={{ ...s.continueBtn, opacity: selected ? 1 : 0.35 }}
          disabled={!selected}
          onClick={() => selected && onSelect(selected)}
        >
          continuar con {info?.nombre || '...'} ✦
        </button>
      </div>
    </div>
  )
}

const makeStyles = (t) => ({
  screen:      { position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 560, background: t.cream, zIndex: 400, display: 'flex', flexDirection: 'column', fontFamily: fonts.body },
  topBar:      { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 20px 16px' },
  closeBtn:    { width: 38, height: 38, borderRadius: 12, border: `1.5px solid ${t.cardBorder}`, background: t.paper, fontSize: 18, cursor: 'pointer', color: t.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 4 },
  heading:     { fontFamily: fonts.display, fontSize: 26, fontWeight: 300, fontStyle: 'italic', color: t.ink, margin: 0 },
  eyebrow:     { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 14, color: t.inkLight, margin: '2px 0 0' },

  pillRow:     { display: 'flex', gap: 8, padding: '0 20px 16px', overflowX: 'auto', scrollbarWidth: 'none', flexShrink: 0 },
  pill:        { flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 14px', borderRadius: 18, border: `1.5px solid ${t.cardBorder}`, background: t.paper, cursor: 'pointer' },
  pillActive:  { borderColor: t.accent, background: t.accent + '18' },
  pillEmoji:   { fontSize: 22 },
  pillLabel:   { fontFamily: fonts.body, fontSize: 11, fontWeight: 600, color: t.ink, letterSpacing: 0.3 },

  detailWrap:  { flex: 1, padding: '0 20px', overflowY: 'auto' },
  detailCard:  { background: t.paper, border: `1.5px solid ${t.cardBorder}`, borderRadius: 24, padding: '22px 20px' },
  detailTop:   { display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 },
  detailEmoji: { fontSize: 48, flexShrink: 0 },
  detailNombre:{ fontFamily: fonts.display, fontSize: 28, fontWeight: 300, color: t.ink, margin: '0 0 6px' },
  detailDesc:  { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 15, color: t.inkMid, lineHeight: 1.5, margin: 0 },
  detailDivider:{ height: 1, background: t.cardBorder, margin: '0 0 14px' },
  detailList:  { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 },
  detailItem:  { fontFamily: fonts.body, fontSize: 14, color: t.inkMid, display: 'flex', gap: 6 },
  detailDot:   { color: t.accent, fontWeight: 700, flexShrink: 0 },

  emptyCard:   { background: t.paper, border: `1.5px dashed ${t.cardBorder}`, borderRadius: 24, padding: '40px 24px', textAlign: 'center' },
  emptyText:   { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 15, color: t.inkLight, margin: 0 },

  footer:      { padding: '16px 20px 40px', flexShrink: 0 },
  continueBtn: { width: '100%', padding: '18px', borderRadius: 22, border: 'none', background: t.cardGradient, color: 'white', fontFamily: fonts.display, fontSize: 20, fontStyle: 'italic', cursor: 'pointer', boxShadow: `0 8px 24px ${t.accentDeep}44` },
})
